import React from "react";

import { faCircleInfo, faPlus, faXmark } from "@fortawesome/pro-regular-svg-icons";

import {
	ButtonIcon,
	ButtonStandard,
	FormInputText,
	FormList,
	FormTextEditor,
	Icon,
	Text,
} from "@performyard/py-components";

import { MAX_COMPETENCY_LEVELS, MAX_LEVEL_DESCRIPTION_LENGTH } from "./validationSchema";

interface LevelItem {
	name: string;
	description: string;
}

const HELPER_TEXT =
	"Competency levels progress from 1 (lowest) to 5 (highest possible). You can add up to 5 levels, but some competencies may top out at a lower level (e.g., Level 3).";
const MAX_REACHED_TEXT = "Maximum number of competency levels has been added";
const LOWEST_LEVEL_LABEL = "(Lowest)";
const HIGHEST_LEVEL_LABEL = "(Highest)";

const levelSuffix = (index: number, total: number): string | null => {
	if (index === 0) return LOWEST_LEVEL_LABEL;
	if (index === total - 1 && total > 1) return HIGHEST_LEVEL_LABEL;
	return null;
};

/**
 * Renders the Competency Levels section of the form: heading, helper text,
 * a FormList-bound array of level cards, and the Add Level control.
 *
 * Each level card binds its name/description fields to `levels[index].name` and
 * `levels[index].description` via FormInputText / FormTextEditor nested paths.
 *
 * `rank` is intentionally NOT tracked in form state — it is stamped at submit
 * time based on array position (see toRequestPayload in validationSchema).
 */
const CompetencyLevelManager: React.FC = () => (
	<FormList<LevelItem>
		id="levels"
		labelPosition="top"
		label={
			<div className="flex flex-col gap-1">
				<Text size="paragraph-1" weight="semi-bold" color="neutral-9">
					Competency Levels
				</Text>
				<Text size="paragraph-3" weight="regular" color="neutral-7">
					{HELPER_TEXT}
				</Text>
			</div>
		}
		input={({ value, pushValue, removeValue }) => {
			const levels = value || [];
			const atMax = levels.length >= MAX_COMPETENCY_LEVELS;

			return (
				// pt-2 adds 8px on top of InputWrapper's own gap-2 (8px) → 16px
				// between the helper-text bottom and the top of the first child,
				// matching Louisa's design (PY-12512 item 5).
				<div className="flex flex-col gap-2 pt-2">
					{levels.map((_, index) => {
						const suffix = levelSuffix(index, levels.length);
						return (
							<div
								key={index}
								data-testid={`level-card-${index}`}
								className="flex flex-col gap-3 rounded-md border border-solid border-neutral-2 bg-white p-4"
							>
								<div className="flex items-center justify-between">
									<div className="flex items-baseline gap-1">
										<Text size="paragraph-2" weight="semi-bold" color="neutral-9">
											{`Level ${index + 1}`}
										</Text>
										{suffix && (
											<Text size="caption" weight="regular" color="neutral-7">
												{suffix}
											</Text>
										)}
									</div>
									<ButtonIcon
										variant="standard"
										color="neutral"
										shade={7}
										size={16}
										icon={faXmark}
										aria-label={`Remove level ${index + 1}`}
										data-testid="remove-level"
										onClick={() => removeValue(index)}
									/>
								</div>
								<FormInputText
									id={`levels[${index}].name`}
									label="Level Name"
									placeholder="Level name here"
								/>
								<FormTextEditor
									id={`levels[${index}].description`}
									label="Description"
									characterLimit={MAX_LEVEL_DESCRIPTION_LENGTH}
									placeholder="Description"
									minHeight={60}
									maxHeight={120}
								/>
							</div>
						);
					})}

					{/* items-start keeps the Add Level button at its intrinsic width
					    (ButtonStandard bakes in `w-full`, so without this the flex-col
					    would stretch it edge-to-edge). Max-reached message sits on its
					    own row below the button per PY-12512 item 9. */}
					<div className="flex flex-col items-start gap-2">
						<div>
							<ButtonStandard
								variant="solid"
								color="primary"
								size="medium"
								iconLeft={faPlus}
								disabled={atMax}
								onClick={() => pushValue({ name: "", description: "" })}
							>
								Add Level
							</ButtonStandard>
						</div>
						{atMax && (
							<div className="flex items-center gap-1">
								<Icon icon={faCircleInfo} color="neutral-7" />
								<Text size="paragraph-3" weight="regular" color="neutral-7">
									{MAX_REACHED_TEXT}
								</Text>
							</div>
						)}
					</div>
				</div>
			);
		}}
	/>
);

export default CompetencyLevelManager;
